// Thin client for the console BFF. Same-origin in prod (Firebase Hosting rewrites /api/** to
// Cloud Run); proxied to :8080 in dev (vite.config). `credentials: 'include'` so the session
// cookie rides along. The BFF is the only authority — tenant scope is derived from the session,
// never sent by the client.

export class ApiError extends Error {
  status: number;
  reason: string;
  constructor(status: number, reason: string) {
    super(reason);
    this.status = status;
    this.reason = reason;
  }
}

// A 401 on a session-gated call means the session expired/was revoked mid-use — the app
// re-authenticates (AuthProvider registers a handler that signs out → Login). The login
// endpoints (/session, /logout) are excluded: a 401 there is part of the sign-in flow, not an
// expired session.
let on_unauthorized: (() => void) | null = null;
export function set_unauthorized_handler(fn: (() => void) | null): void {
  on_unauthorized = fn;
}
const AUTH_ENDPOINTS = new Set(['/session', '/logout']);

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && !AUTH_ENDPOINTS.has(path)) {
    on_unauthorized?.();
  }
  if (!res.ok) {
    let reason = 'request_failed';
    try {
      reason = ((await res.json()) as { reason?: string }).reason ?? reason;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, reason);
  }
  return (await res.json()) as T;
}

export interface SessionInfo {
  tenant_id: string;
  tenant_name: string;
  first_name: string;
  role: 'owner' | 'admin' | 'member';
  apps: string[];
}

// Exchange a Firebase ID token for a console session (sets the cookie). Returns the tenant
// context, or throws ApiError(403) when the identity matches no customer (no self-signup).
export const exchange_session = (id_token: string) =>
  request<SessionInfo>('POST', '/session', { id_token });

export interface Me extends SessionInfo {
  user_id: string;
}
export const get_me = () => request<Me>('GET', '/me');

export const logout = () => request<{ ok: true }>('POST', '/logout');

// --- Billing — Stripe payment link + price for the account Billing page. The URL is served by
// the BFF (Cloud Run env var), never hardcoded in the component. payment_link_url is null when
// no link is configured (the page then shows only the contact option) ---

// One purchasable plan option (tier × interval) with its live price, for the picker cards.
export interface BillingPlanOption {
  plan: string; // slug
  tier: string; // 'starter' | 'plus'
  interval: string; // 'month' | 'year'
  name: string;
  price_label: string;
}

export interface BillingConfig {
  // `checkout` = backend Stripe Checkout (POST /billing/checkout); `payment_link` = legacy
  // buy.stripe.com URL; `none` = no CTA. Optional for back-compat with an older BFF that omits it.
  mode?: 'checkout' | 'payment_link' | 'none';
  plans?: BillingPlanOption[]; // checkout mode: resolvable offered plans (cards/toggle source)
  default_plan?: string | null;
  plan?: string | null; // back-compat single-plan field
  payment_link_url: string | null;
  price_label: string;
  plan_name: string;
}
export const get_billing_config = () => request<BillingConfig>('GET', '/billing/config');

// Mint a Stripe Checkout Session for the account subscription; returns the hosted URL to redirect to.
export const create_checkout_session = (plan?: string) =>
  request<{ checkout_url: string }>('POST', '/billing/checkout', plan ? { plan } : {});

// --- Members section (docs/archive/onboarding-phase2.md §3) — owner/admin only; the BFF derives the
// tenant from the session claim and enforces same-tenant on every action ---

export interface MemberInfo {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'pending' | 'deactivated';
  created_at: string | null;
  requested_app_code?: string;
  // Present on a pending join when approve-and-enable applies (tenant-scope app, active in
  // the registry, no grant yet). Advisory — the BFF re-validates at approval time (§6c).
  enable_app_offer?: { app_code: string; display_name: string };
}

export const get_members = () => request<{ users: MemberInfo[] }>('GET', '/members');
export const approve_member = (user_id: string, enable_app?: boolean) =>
  request<{ ok: true; enabled_app?: string }>(
    'POST',
    `/members/${encodeURIComponent(user_id)}/approve`,
    enable_app !== undefined ? { enable_app } : {},
  );
export const decline_member = (user_id: string) =>
  request<{ ok: true }>('POST', `/members/${encodeURIComponent(user_id)}/decline`, {});
export const set_member_status = (user_id: string, status: 'active' | 'deactivated') =>
  request<{ ok: true }>('POST', `/members/${encodeURIComponent(user_id)}/status`, { status });
export const set_member_role = (user_id: string, role: 'admin' | 'member') =>
  request<{ ok: true }>('POST', `/members/${encodeURIComponent(user_id)}/role`, { role });

// --- LinkedIn app-module — read-only connection status (mutations live at linkedin.captomi.com) ---
export interface LinkedinConnections {
  personal: { connected: boolean; author_urn?: string; status?: string };
  company_page: { connected: boolean; name?: string };
  blog: { connected: boolean; provider?: string; detail?: string };
  tracker: { connected: boolean; provider?: string; repo?: string };
  review: { enabled: boolean; provider?: string };
  manage_url: string;
}
export const get_linkedin_connections = () =>
  request<LinkedinConnections>('GET', '/linkedin/connections');

// --- office app-module (Phase 3) — all tenant-scoped server-side from the session ---
import type {
  BookingsListResponse,
  BookingDetailResponse,
  ResendResponse,
  CancelResponse,
  UsageResponse,
} from './apps/office/types';

export const get_office_bookings = (filters?: Record<string, string>) => {
  const qs = new URLSearchParams(filters ?? {}).toString();
  return request<BookingsListResponse>('GET', `/office/bookings${qs ? `?${qs}` : ''}`);
};
export const get_office_booking = (booking_id: string) =>
  request<BookingDetailResponse>('GET', `/office/bookings/${encodeURIComponent(booking_id)}`);
export const resend_office_booking = (booking_id: string, channels?: ('email' | 'sms')[]) =>
  request<ResendResponse>(
    'POST',
    `/office/bookings/${encodeURIComponent(booking_id)}/resend`,
    channels ? { channels } : {},
  );
export const cancel_office_booking = (booking_id: string, reason?: string) =>
  request<CancelResponse>(
    'POST',
    `/office/bookings/${encodeURIComponent(booking_id)}/cancel`,
    reason ? { reason } : {},
  );
export const get_office_usage = () => request<UsageResponse>('GET', '/office/usage');

// --- Google Calendar connect (Flow-2, office google-calendar-spec §3.4) ---
// A FULL-PAGE navigation, not a fetch: the BFF mints a signed launch token and 302-redirects to
// office's OAuth start, which then redirects to Google. The browser must follow the whole chain,
// so we hand the tab to the BFF endpoint rather than XHR it. The endpoint is session-cookie
// authenticated (top-level GET carries `__session`), so no token is constructed client-side.
export const CALENDAR_CONNECT_PATH = '/api/office/calendar/connect';
export function connect_google_calendar(): void {
  window.location.assign(CALENDAR_CONNECT_PATH);
}

// --- office schema-driven editing (Phase 4 / F3) ---
import type { FieldNode } from '@captomi/schema-form';

export const get_office_schema = () => request<{ schema: FieldNode }>('GET', '/office/schema');
export const get_office_app_data = () => request<{ data: Record<string, unknown> }>('GET', '/office/app-data');
export const patch_office_app_data = (patch: Record<string, unknown>) =>
  request<{ data: Record<string, unknown> }>('PATCH', '/office/app-data', { patch });
// Stamp the one-time Get Started "wizard seen" marker (server-writes profile.get_started_wizard_seen_at).
export const mark_get_started_seen = () =>
  request<{ ok: true }>('POST', '/office/get-started/seen', {});
